/**
 * User-facing error messages for the service type management module.
 * Covers validation (name length, uniqueness), auth, and network failures.
 * Used by `createServiceTypeApiService`, `editServiceTypeApiService`, and related hooks.
 */
export const SERVICE_TYPE_ERROR_MESSAGES = {
  NO_PERMISSION_TOOLTIP: 'Nie masz uprawnień do wykonania tej czynności',
  REQUIRED: 'To pole jest wymagane',
  NAME_TOO_LONG: 'Nazwa typu usługi nie może przekraczać 50 znaków.',
  DESCRIPTION_TOO_LONG: 'Opis nie może przekraczać 500 znaków.',
  NAME_ALREADY_EXISTS: 'Taka nazwa typu usługi już istnieje',
  VALIDATION_ERROR: 'Nieprawidłowe dane. Sprawdź pola i spróbuj ponownie.',
  UNAUTHORIZED: 'Brak uprawnień do wykonania tej operacji.',
  NETWORK_ERROR: 'Błąd sieci. Sprawdź połączenie i spróbuj ponownie.',
  INTERNAL_SERVER_ERROR: 'Błąd serwera. Spróbuj ponownie później.',
  DEFAULT_ERROR: 'Wystąpił błąd. Spróbuj ponownie.',
  UNEXPECTED_ERROR: 'Nieoczekiwany błąd. Spróbuj ponownie.',
} as const;

/**
 * User-facing error messages for the view service request detail page.
 * Covers auth, network, and generic load failures.
 * Used by `viewServiceRequestApiService` and `useViewServiceRequest`.
 */
export const VIEW_SERVICE_REQUEST_ERROR_MESSAGES = {
  UNAUTHORIZED: 'Brak uprawnień do wykonania tej operacji.',
  NETWORK_ERROR: 'Błąd sieci. Sprawdź połączenie i spróbuj ponownie.',
  INTERNAL_SERVER_ERROR: 'Błąd serwera. Spróbuj ponownie później.',
  DEFAULT_ERROR: 'Wystąpił błąd. Spróbuj ponownie.',
  UNEXPECTED_ERROR: 'Nieoczekiwany błąd. Spróbuj ponownie.',
} as const;

/**
 * User-facing error messages for the assign-technician modal.
 * Covers validation, auth, concurrent-assignment conflicts, status-change races,
 * and eligible-technician fetch failures.
 * Used by `assignTechnicianApiService` and `useAssignTechnician`.
 */
export const ASSIGN_TECHNICIAN_ERROR_MESSAGES = {
  REQUIRED: 'To pole jest wymagane',
  NOTES_TOO_LONG: 'Notatka nie może przekraczać 500 znaków.',
  UNAUTHORIZED: 'Brak uprawnień do wykonania tej operacji.',
  NETWORK_ERROR: 'Błąd sieci. Sprawdź połączenie i spróbuj ponownie.',
  INTERNAL_SERVER_ERROR: 'Błąd serwera. Spróbuj ponownie później.',
  DEFAULT_ERROR: 'Nie udało się przydzielić technika. Spróbuj ponownie.',
  UNEXPECTED_ERROR: 'Nieoczekiwany błąd. Spróbuj ponownie.',
  ELIGIBLE_FETCH_ERROR:
    'Nie udało się pobrać listy dostępnych techników. Spróbuj ponownie.',
  STATUS_CHANGED:
    'Status zgłoszenia został zmieniony. Odśwież stronę i spróbuj ponownie.',
  TECHNICIAN_CONFLICT: 'Ten technik został już przydzielony',
  ADMIN_CONFLICT: 'Ten administrator został już przydzielony',
} as const;

export const RESCHEDULE_SR_ERROR_MESSAGES = {
  REQUIRED: 'To pole jest wymagane',
  NOTES_TOO_LONG: 'Notatka nie może przekraczać 500 znaków.',
  UNAUTHORIZED: 'Brak uprawnień do wykonania tej operacji.',
  NETWORK_ERROR: 'Błąd sieci. Sprawdź połączenie i spróbuj ponownie.',
  INTERNAL_SERVER_ERROR: 'Błąd serwera. Spróbuj ponownie później.',
  DEFAULT_ERROR: 'Nie udało się zmienić terminu. Spróbuj ponownie.',
  UNEXPECTED_ERROR: 'Nieoczekiwany błąd. Spróbuj ponownie.',
  STATUS_CHANGED:
    'Status zgłoszenia został zmieniony. Odśwież stronę i spróbuj ponownie.',
  TECHNICIAN_CONFLICT:
    'Technik jest niedostępny — wybrany termin koliduje z innym przydziałem.',
  ADMIN_CONFLICT:
    'Administrator jest niedostępny — wybrany termin koliduje z innym przydziałem.',
  WITHIN_24H:
    'Zmiana terminu nie jest dozwolona w ciągu 24 godzin przed wizytą.',
  NOT_RESCHEDULABLE:
    'Tego zgłoszenia nie można już zmienić w obecnym statusie.',
} as const;

/**
 * User-facing error messages for the update-service-request-status modal (admin only).
 * Backend status names in 409 messages are localised to Polish by
 * `mapStatusNames` in `updateServiceRequestStatusApiService`.
 * Used by `updateServiceRequestStatusApiService` and `useUpdateServiceRequestStatus`.
 */
export const UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES = {
  REQUIRED: 'To pole jest wymagane',
  NOTES_TOO_LONG: 'Notatka nie może przekraczać 500 znaków.',
  UNAUTHORIZED: 'Brak uprawnień do wykonania tej operacji.',
  NETWORK_ERROR: 'Błąd sieci. Sprawdź połączenie i spróbuj ponownie.',
  INTERNAL_SERVER_ERROR: 'Błąd serwera. Spróbuj ponownie później.',
  DEFAULT_ERROR: 'Nie udało się zaktualizować statusu. Spróbuj ponownie.',
  UNEXPECTED_ERROR: 'Nieoczekiwany błąd. Spróbuj ponownie.',
} as const;

/**
 * User-facing error messages for the cancel service request flow (client only).
 *
 * @remarks
 * `CANCELLATION_WINDOW` maps the backend `CancellationWindowError` (HTTP 409)
 * — shown when a scheduled visit is less than 24 hours away.
 * `TRANSITION_NOT_ALLOWED` maps any other HTTP 409 (terminal status or same-status transition).
 * `UNAUTHORIZED` maps HTTP 403, covering both missing AVP permission and cross-client ownership violations.
 *
 * Used by `cancelServiceRequestApiService` and `useCancelServiceRequest`.
 */
export const CANCEL_SERVICE_REQUEST_ERROR_MESSAGES = {
  UNAUTHORIZED: 'Brak uprawnień do anulowania tego zgłoszenia.',
  NOT_FOUND: 'Zgłoszenie serwisowe nie zostało znalezione.',
  CANCELLATION_WINDOW:
    'Tego zgłoszenia nie można już anulować, ponieważ do zaplanowanej wizyty pozostało mniej niż 24 godziny.',
  TRANSITION_NOT_ALLOWED:
    'Zmiana statusu na "anulowane" nie jest dozwolona z obecnego stanu.',
  NETWORK_ERROR: 'Błąd sieci. Sprawdź połączenie i spróbuj ponownie.',
  INTERNAL_SERVER_ERROR: 'Błąd serwera. Spróbuj ponownie później.',
  DEFAULT_ERROR: 'Nie udało się anulować zgłoszenia. Spróbuj ponownie.',
  UNEXPECTED_ERROR: 'Nieoczekiwany błąd. Spróbuj ponownie.',
} as const;

/**
 * User-facing error messages for the technician acknowledge (accept/reject) flow.
 * Covers validation, permission, stale-status, and network failures.
 * Used by `acknowledgeServiceRequestApiService` and `useAcknowledgeServiceRequest`.
 */
export const ACKNOWLEDGE_SR_ERROR_MESSAGES = {
  /** Field is required */
  REASON_REQUIRED: 'To pole jest wymagane',
  /** Reason must not exceed 500 characters */
  REASON_TOO_LONG: 'Powód nie może przekraczać 500 znaków.',
  /** Insufficient permissions to perform this action */
  PERMISSION_DENIED: 'Brak uprawnień do wykonania tej czynności.',
  /** This request can no longer be accepted or rejected (not in ASSIGNED status) */
  ACTION_WINDOW_EXPIRED:
    'This service request has already been accepted or rejected.',
  /** Network error — check connection and try again */
  NETWORK_ERROR: 'Błąd sieci. Sprawdź połączenie i spróbuj ponownie.',
  /** Server error — try again later */
  INTERNAL_SERVER_ERROR: 'Błąd serwera. Spróbuj ponownie później.',
  /** Could not confirm the task — try again */
  DEFAULT_ERROR: 'Nie udało się potwierdzić zadania. Spróbuj ponownie.',
  /** Unexpected error — try again */
  UNEXPECTED_ERROR: 'Nieoczekiwany błąd. Spróbuj ponownie.',
} as const;
